import Footer from "../landing-page/components/Footer";
import NinjaTraderTrademarkNotice from "../client/components/legal/NinjaTraderTrademarkNotice";

/**
 * Public /disclosures page — canonical reference for every regulatory
 * statement we link to from social-media bios (deep-links like
 * /disclosures#risk).
 *
 * NinjaTrader Vendor Program: the five disclosure texts below are English
 * VERBATIM per the Vendor Compliance Guidelines. They must stay English in both
 * the FR and EN UI — do NOT route them through i18n, translate, reword, or
 * abbreviate. Section 5 reuses <NinjaTraderTrademarkNotice> (same verbatim text).
 *
 * Each <section> carries an anchor id + scroll-mt so deep-links land below any
 * sticky chrome. The global <Footer> (which itself carries the Risk Disclosure
 * at the App root) renders normally at the bottom — that duplication is expected.
 */

type DisclosureSectionProps = {
  id: string;
  title: string;
  children: React.ReactNode;
};

function DisclosureSection({ id, title, children }: DisclosureSectionProps) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
        {title}
      </h2>
      <div className="mt-4 text-base leading-7 text-foreground/80">
        {children}
      </div>
    </section>
  );
}

export default function DisclosuresPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <main className="isolate">
        <div className="mx-auto max-w-4xl px-6 py-16 lg:px-8 lg:py-24">
          <h1 className="text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
            Disclosures
          </h1>

          <div className="mt-12 space-y-12">
            {/* Section 1 — Risk Disclosure (verbatim, mirrors RiskDisclosure) */}
            <DisclosureSection id="risk" title="Risk Disclosure">
              <p>
                Futures and forex trading contains substantial risk and is not
                for every investor. An investor could potentially lose all or
                more than the initial investment. Risk capital is money that can
                be lost without jeopardizing one's financial security or life
                style. Only risk capital should be used for trading and only
                those with sufficient risk capital should consider trading. Past
                performance is not necessarily indicative of future results.
              </p>
            </DisclosureSection>

            {/* Section 2 — Hypothetical Performance Disclosure (verbatim) */}
            <DisclosureSection
              id="hypothetical"
              title="Hypothetical Performance Disclosure"
            >
              <p>
                Hypothetical performance results have many inherent limitations,
                some of which are described below. No representation is being
                made that any account will or is likely to achieve profits or
                losses similar to those shown; in fact, there are frequently
                sharp differences between hypothetical performance results and
                the actual results subsequently achieved by any particular
                trading program. One of the limitations of hypothetical
                performance results is that they are generally prepared with the
                benefit of hindsight. In addition, hypothetical trading does not
                involve financial risk, and no hypothetical trading record can
                completely account for the impact of financial risk of actual
                trading. For example, the ability to withstand losses or to
                adhere to a particular trading program in spite of trading losses
                are material points which can also adversely affect actual
                trading results. There are numerous other factors related to the
                markets in general or to the implementation of any specific
                trading program which cannot be fully accounted for in the
                preparation of hypothetical performance results and all which can
                adversely affect trading results.
              </p>
            </DisclosureSection>

            {/* Section 3 — Testimonials (verbatim) */}
            <DisclosureSection id="testimonials" title="Testimonials">
              <p>
                Testimonials appearing on this website may not be representative
                of other clients or customers and is not a guarantee of future
                performance or success.
              </p>
            </DisclosureSection>

            {/* Section 4 — Digital Currencies (verbatim) */}
            <DisclosureSection id="digital-currencies" title="Digital Currencies">
              <p>
                View CFTC advisories as they contain more information on the
                risks associated with trading virtual currencies.
              </p>
            </DisclosureSection>

            {/* Section 5 — Trademark Notice (verbatim, reuses the shared component) */}
            <DisclosureSection id="trademark" title="Trademark Notice">
              <NinjaTraderTrademarkNotice />
            </DisclosureSection>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
